import type { FunctionComponent, MouseEvent } from 'react';
import React, { useCallback, useMemo } from 'react';

import { Analytics, formatContainers } from '@trello/atlassian-analytics';
import type { CardLabelType } from '@trello/labels';
import { CompactCardLabel, showLabelsState, sortLabels } from '@trello/labels';
import { isBoardRoute, useRouteId } from '@trello/routes';
import { useSharedState } from '@trello/shared-state';

import { stopPropagationAndPreventDefault } from 'app/src/stopPropagationAndPreventDefault';
import { useCardFrontLabelsQuery } from './CardFrontLabelsQuery.generated';

import styles from './CardFrontLabels.less';

interface ColoredLabel extends Omit<CardLabelType, 'color'> {
  color: NonNullable<CardLabelType['color']>;
}

interface Props {
  /**
   * idCard can be undefined when the card is first created, and this component
   * renders optimistically before the card actually has an entry in server.
   */
  idCard?: string;
  /**
   * Optional argument to override the labels normally returned via query.
   * Useful for showing labels that aren't necessarily tied to a backend.
   * One example is labels rendered by the Card Composer during card creation,
   * where idCard doesn't yet exist but labels can be preemptively added.
   */
  labelsOverride?: CardLabelType[];
}

export const CardFrontLabels: FunctionComponent<Props> = ({
  idCard = '',
  labelsOverride,
}) => {
  const routeId = useRouteId();
  const { data } = useCardFrontLabelsQuery({
    variables: {
      idCard,
    },
    skip: !idCard || !!labelsOverride,
    waitOn: [isBoardRoute(routeId) ? 'CurrentBoardFull' : 'None'],
  });
  const { idBoard, idList, labels } = data?.card ?? {};

  const sortedLabels = useMemo(
    () =>
      sortLabels(labelsOverride ?? labels).filter(
        (l): l is ColoredLabel => !!l.color,
      ),
    [labelsOverride, labels],
  );

  const [{ showText }, setShowText] = useSharedState(showLabelsState);

  const onClick = useCallback(
    (e: MouseEvent) => {
      if (e.ctrlKey || e.metaKey || e.shiftKey) {
        return;
      }
      stopPropagationAndPreventDefault(e);
      setShowText((state) => ({ showText: !state.showText }));
      Analytics.sendTrackEvent({
        action: 'toggled',
        actionSubject: 'labelText',
        source: 'cardView',
        containers: formatContainers({ idCard, idBoard, idList }),
        attributes: { visibility: showText },
      });
    },
    [idCard, idBoard, idList, showText, setShowText],
  );

  if (!sortedLabels.length) {
    return null;
  }

  return (
    <div className={styles.labels}>
      {sortedLabels.map((label) => (
        <div key={label.id} className={styles.labelWrapper}>
          <CompactCardLabel
            className={styles.label}
            label={label}
            onClick={onClick}
            tabIndex={-1}
          />
        </div>
      ))}
    </div>
  );
};
